import { getDeviceId } from "@amplitude/analytics-browser";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getAccountNumber } from "@urp/store-selectors";
import { fetchVariantForUser } from "./experimentClient";
import { DEPLOYMENT_KEY, getDeploymentKey } from "./deploymentKey";
import { VariantEnum } from "./types";
import useExperiments from "./useExperiments";

export { DEPLOYMENT_KEY, getDeploymentKey };

export const useIsTreatmentVariant = (experimentKey: string) => {
  const { getVariant } = useExperiments(DEPLOYMENT_KEY);
  const variant = getVariant<VariantEnum, boolean>(experimentKey);

  return variant?.value === VariantEnum.TREATMENT;
};

/**
 * Returns whether the current user is in the treatment group for
 * `experimentKey`. All consumers share a single Amplitude Experiment client
 * (see `./experimentClient`).
 *
 * Network behavior:
 *  - `fetchVariantForUser` is called on mount and whenever the effect deps
 *    (`accountNumber`/`deviceId`/`experimentKey`) change. Deduplication is
 *    handled by `experimentClient`: concurrent calls share one in-flight
 *    request and resolved results are cached per identity, keyed on
 *    `accountNumber` only. A `deviceId` transition (undefined -> UUID as
 *    Amplitude initialises) therefore re-runs the effect but does not trigger a
 *    new network request.
 *  - A full page refresh re-runs the singleton init and triggers a fresh fetch.
 *
 * Fail-closed semantics:
 *  - `isResolved` is false during the in-flight window; consumers that fire
 *    side-effects based on the flag should wait for `isResolved` before acting.
 *  - Results are keyed to the current identity so stale state from a previous
 *    user never bleeds through between renders.
 *  - A failed fetch keeps `isTreatment` false and flips `isResolved` to true
 *    so consumers fail closed rather than blocking indefinitely.
 */
export const useIsTreatmentVariantByUser = (
  experimentKey: string
): { isTreatment: boolean; isResolved: boolean } => {
  const accountNumber = useSelector(getAccountNumber);
  const deviceId = getDeviceId();
  const identity = `${accountNumber ?? ""}:${experimentKey}`;

  const [resolvedState, setResolvedState] = useState<{
    forIdentity: string;
    isTreatment: boolean;
    isResolved: boolean;
  }>({ forIdentity: "", isTreatment: false, isResolved: false });

  useEffect(() => {
    if (!accountNumber) return undefined;

    let cancelled = false;

    fetchVariantForUser(accountNumber, deviceId, experimentKey)
      .then((v) => {
        if (!cancelled) {
          setResolvedState({
            forIdentity: identity,
            isTreatment: v?.value === VariantEnum.TREATMENT,
            isResolved: true
          });
        }
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error("[amplitude] fetchVariantForUser failed:", e);
        if (!cancelled) {
          setResolvedState({
            forIdentity: identity,
            isTreatment: false,
            isResolved: true
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accountNumber, deviceId, experimentKey]);

  // If the stored result is for a different identity, treat as unresolved to
  // prevent stale state from a previous user bleeding through between renders.
  if (resolvedState.forIdentity !== identity) {
    return { isTreatment: false, isResolved: false };
  }
  return {
    isTreatment: resolvedState.isTreatment,
    isResolved: resolvedState.isResolved
  };
};
